// shared/team.js — the roles as people read them, and the team as a visit
// shows it (Jobs and Schedule v1, stage 5, ADR-041).
//
// What each role may DO is server/roles.js's matrix, and nowhere else. This is
// what each one is CALLED and what a person is told it means, and the rules the Schedule and a booking use to show the team: whether
// there is one, who a visit can be given to, the person menu, initials.
//
// SOLE TRADER FIRST. A company of one sees no team at all: no "Assigned to",
// no person menu, no initials, no "Not assigned". All of it appears with the
// second person, whatever their role, a Viewer included: a team is two or more
// people on the company, because that is when "who is this visit for" has
// more than one answer anybody could ask about.
//
// MORE THAN ONE PERSON ON A VISIT (16 October 2026). An engineer often goes
// with their apprentice, and the apprentice should see the visit on their own
// day. So a visit has a set of people, in the order they were chosen
// (`assigneeUserIds`), and `assigneeUserId` is the first of them, which is
// all a build from before could read or send. Still one visit, with one Start
// and one Finish; two people's different times are two visits.
//
// Pure: imports only from shared/, no DOM, no node builtins.

import { inAList } from "./client-people.js";

/** The roles somebody can be invited as, in the order a picker offers them. */
export const INVITABLE_ROLES = Object.freeze(["member", "admin", "viewer", "field"]);

// No role is paid for (ADR-042): the owner's subscription is the company's
// one paid place, and everyone invited, in any role, is included. So nothing
// here says which roles cost money, and no role's line says "Free".

/** Each role's name and the one line that says what it means, as the Team screens show them. */
export const ROLE_WORDS = Object.freeze({
  owner: { label: "Owner", description: "Everything, including the plan and the company itself." },
  admin: { label: "Admin", description: "Everything but cancelling the plan, handing over or deleting the company." },
  member: { label: "Member", description: "Makes and sends invoices, quotes and jobs." },
  viewer: { label: "Viewer", description: "Sees everything and changes nothing." },
  field: { label: "Field", description: "Sees their own visits and jobs, and no prices." }
});

/** A role's name, "Member" for one this build doesn't know. */
export function roleLabel(role) {
  return ROLE_WORDS[role]?.label ?? "Member";
}

export const TEAM_COPY = Object.freeze({
  assignedTo: "Assigned to",
  whoIsGoing: "Who’s going",
  notAssigned: "Not assigned",
  noLongerOnTeam: "No longer on the team",
  you: "You",
  everyone: "Everyone",
  me: "Me",
  show: "Show",
  // A Field person's two tabs, and what their app says instead of prices.
  myJobs: "My jobs",
  noMyJobs: "No jobs yet",
  noMyJobsHint: "Jobs appear here when a visit is assigned to you.",
  noMoney: "No prices, quotes or invoices for a Field person.",
  // Billing, wherever it is said (ADR-042): one price, the whole team.
  wholeTeamIncluded: "Your whole team is included."
});

/**
 * Whether a company is a team: more than one person on it, of any role.
 * `people` are the active members as `/sync/pull` serves them
 * (`{ userId, name, initials, role, mayWorkVisits }`).
 */
export function isTeam(people = []) {
  return (Array.isArray(people) ? people : []).length > 1;
}

function list(people) {
  return Array.isArray(people) ? people.filter((p) => p && p.userId) : [];
}

/** The most people one visit can have on it. */
export const VISIT_PEOPLE_MAX = 20;

/**
 * The people on a visit, as user ids in the order they were chosen: its
 * `assigneeUserIds`, or, from a row that predates them, its one
 * `assigneeUserId`. Each once, never empty strings.
 */
export function visitPeopleIds(visit) {
  const raw = Array.isArray(visit?.assigneeUserIds)
    ? visit.assigneeUserIds
    : visit?.assigneeUserId ? [visit.assigneeUserId] : [];
  const seen = new Set();
  const out = [];
  for (const id of raw) {
    const key = String(id ?? "").trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

/** Whether this person is anywhere on the visit. */
export function isOnVisit(visit, userId) {
  return !!userId && visitPeopleIds(visit).includes(userId);
}

/** Whether two lists of people are the same people, whatever their order. */
export function samePeople(a = [], b = []) {
  const x = new Set(a);
  const y = new Set(b);
  if (x.size !== y.size) return false;
  for (const id of x) if (!y.has(id)) return false;
  return true;
}

/**
 * The people a save from a build before more than one person leaves on a
 * visit. Such a build knows only `assigneeUserId`, the first person, so it
 * changes the first and keeps the rest: what it never showed it can't have
 * meant to take off, the way `keepUnitsFromOlderBuild` keeps a line's unit.
 * `null` takes the first person off and leaves the others.
 */
export function keepVisitPeopleFromOlderBuild(beforeIds = [], assigneeUserId = null) {
  const rest = (Array.isArray(beforeIds) ? beforeIds : []).slice(1);
  const first = String(assigneeUserId ?? "").trim();
  if (!first) return rest;
  return [first, ...rest.filter((id) => id !== first)];
}

/**
 * Who a visit can be assigned to, as Book and Move offer them: You first,
 * then everyone else who works visits (not a Viewer), in the order given,
 * which is the owner first and then oldest first. Empty for a company of one,
 * which shows no picker. `me` is the signed-in person's user id. Any number
 * of them may be on one visit.
 */
export function assigneeChoices(people = [], me = null) {
  const all = list(people);
  if (!isTeam(all)) return [];
  const mine = all.find((p) => p.userId === me);
  const others = all.filter((p) => p.userId !== me && p.mayWorkVisits !== false);
  return [
    ...(mine && mine.mayWorkVisits !== false ? [{ userId: mine.userId, label: TEAM_COPY.you, initials: mine.initials || "" }] : []),
    ...others.map((p) => ({ userId: p.userId, label: p.name || "Member", initials: p.initials || "" }))
  ];
}

/**
 * The names of a visit's people as a team reads them, in order: "You" for
 * the signed-in person, else their name; somebody who has left is dropped
 * unless they were the only one.
 */
function peopleNames(ids, all, me) {
  const named = ids.map((id) => (id === me ? TEAM_COPY.you : all.find((p) => p.userId === id)?.name || null));
  const known = named.filter(Boolean);
  if (!known.length && ids.length) return [TEAM_COPY.noLongerOnTeam];
  // "You" first however they were chosen: "You and Callum", never "Callum and You".
  return [...known.filter((n) => n === TEAM_COPY.you), ...known.filter((n) => n !== TEAM_COPY.you)];
}

/**
 * Who is on a visit, in words, for its screen's "Assigned to" and the Who’s
 * going row: "You", a name, "You and Callum", "Sam, Callum, and Pat", "Not
 * assigned", or "No longer on the team" for somebody who has left. Empty for
 * a company of one, which shows no such row.
 */
export function assigneeLabel(visit, people = [], me = null) {
  const all = list(people);
  if (!isTeam(all)) return "";
  const ids = visitPeopleIds(visit);
  if (!ids.length) return TEAM_COPY.notAssigned;
  return inAList(peopleNames(ids, all, me));
}

/**
 * The same people as a sentence finishes them, for VoiceOver and a screen
 * reader: "with you and Callum", "with Pat"; "" for nobody, for somebody who
 * has left, and for a company of one.
 */
export function visitWithWords(visit, people = [], me = null) {
  const all = list(people);
  if (!isTeam(all)) return "";
  const ids = visitPeopleIds(visit);
  const names = peopleNames(ids, all, me).filter((n) => n !== TEAM_COPY.noLongerOnTeam);
  if (!names.length) return "";
  return `with ${inAList(names.map((n) => (n === TEAM_COPY.you ? "you" : n)))}`;
}

/**
 * The initials a visit shows on the Schedule and its job: its first person's,
 * yours included, or "" when nobody is on it or the company is one person.
 * `visitInitialsList` is all of them, for the overlapping badges.
 *
 * YOURS TOO (Andy's device review, 15 October 2026). With only other
 * people's visits marked, the owner's own visit and Callum's on the same job
 * read as one visit twice: the same name, one row with "CR" and one with
 * nothing. On a team every visit with somebody on it says who. `me` is no
 * longer read, and stays in the signature for the callers that pass it.
 */
export function visitInitials(visit, people = [], me = null) {
  return visitInitialsList(visit, people, me)[0] || "";
}

/**
 * Every person's initials on a visit, in the order they were chosen, for
 * the stack of badges: [] for nobody and for a company of one. Somebody who
 * has left has none to show and is left out.
 */
export function visitInitialsList(visit, people = [], _me = null) {
  const all = list(people);
  if (!isTeam(all)) return [];
  return visitPeopleIds(visit)
    .map((id) => all.find((p) => p.userId === id)?.initials || "")
    .filter(Boolean);
}

/**
 * The badges a row has room for: the first `shown` people's initials, and
 * "+N" for the rest, so a row keeps its height whoever is on it.
 * `{ initials: ["SC", "CP"], userIds: [the two people], more: 1, moreLabel: "+1" }`.
 */
export function visitBadges(visit, people = [], _me = null, { shown = 2 } = {}) {
  const all = list(people);
  const known = isTeam(all)
    ? visitPeopleIds(visit).map((id) => ({ id, initials: all.find((p) => p.userId === id)?.initials || "" })).filter((p) => p.initials)
    : [];
  const room = Math.max(1, Math.floor(Number(shown) || 2));
  const showing = known.slice(0, room);
  const more = known.length - showing.length;
  return { initials: showing.map((p) => p.initials), userIds: showing.map((p) => p.id), more, moreLabel: more > 0 ? `+${more}` : "" };
}

/**
 * The Schedule's person menu: Everyone, Me, then each other person who works
 * visits, as `{ key, label }` (`key` is "everyone", "me" or a user id). Empty
 * for a company of one, which shows no menu, and for a Field person, whose
 * Schedule is only ever their own visits: the people they're served are the
 * ones they go with, to name on those visits, not a team to choose from.
 */
export function scheduleFilters(people = [], me = null) {
  const all = list(people);
  if (!isTeam(all)) return [];
  if (all.find((p) => p.userId === me)?.role === "field") return [];
  return [
    { key: "everyone", label: TEAM_COPY.everyone },
    { key: "me", label: TEAM_COPY.me },
    ...all.filter((p) => p.userId !== me && p.mayWorkVisits !== false).map((p) => ({ key: p.userId, label: p.name || "Member" }))
  ];
}

/** Whether a visit shows under a person-menu choice: everyone's, or one a person is anywhere on. */
export function visitShownFor(visit, key = "everyone", me = null) {
  if (!key || key === "everyone") return true;
  if (key === "me") return isOnVisit(visit, me);
  return isOnVisit(visit, key);
}
